import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { AutoCompleteSelectUI, PhoneBase, TabsUI } from '../../Interface';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { useLoader } from '../../../provider/LoaderProvider';
import { WmsType } from '../../../config/wms/wmsType';
import wmsBaseService from '../../../service/wms/wmsBase.service';
import itemService from '../../../service/item.service';
import IncomingItem from '../Components/IncomingItem';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems } from '../../../utils/helper';
import { InvManagementMethod } from '../../../config/itemGroup';
import useConfig from '../../../context/ConfigContext';
import {
  NumberFormatKind,
  getConfigDecimalPlacesByKind,
  getInputStepByDecimalPlaces,
  roundToDecimalPlaces,
} from '../../../utils/numberFormatHelper';
import WmsTabContent from './WmsTabContent';
import TargetDocTab from './TargetDocTab';
import PurchaseDeliveryBaseDocTab from './PurchaseDeliveryBaseDocTab';

const wmsType = WmsType.PurchaseDelivery;

const PurchaseDeliveryDoc = () => {
  const navigate = useNavigate();
  const { wmsEntry } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { config, getConfig } = useConfig();

  const [activeTab, setActiveTab] = useState(0);
  const [head, setHead] = useState(null);
  const [lines, setLines] = useState([]);
  const [baseDocLines, setBaseDocLines] = useState([]);

  const [incomingFilter, setIncomingFilter] = useState('');
  const [incomingItem, setIncomingItem] = useState(null);
  const [incomingQty, setIncomingQty] = useState('');
  const [incomingBatchSn, setIncomingBatchSn] = useState('');
  const [incomingLot, setIncomingLot] = useState('');

  const [warehouseData, setWarehouseData] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [sourceBinId, setSourceBinId] = useState(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');

  const resolvedHeadId = useMemo(() => Number(wmsEntry), [wmsEntry]);
  const quantityDecimalPlaces = useMemo(
    () => getConfigDecimalPlacesByKind(NumberFormatKind.QUANTITY, config, 2),
    [config]
  );
  const quantityInputStep = useMemo(() => getInputStepByDecimalPlaces(quantityDecimalPlaces), [quantityDecimalPlaces]);

  const fetchBaseDocLines = async (baseId = head?.baseId) => {
    if (!baseId) {
      setBaseDocLines([]);
      return;
    }

    const baseRes = await wmsBaseService.getBaseViewByWmsId(baseId, { wmsType });
    const nextBaseDocLines = Array.isArray(baseRes?.rows)
      ? baseRes.rows
      : Array.isArray(baseRes?.docLine)
        ? baseRes.docLine
        : Array.isArray(baseRes)
          ? baseRes
          : [];
    setBaseDocLines(nextBaseDocLines);
  };

  const fetchHeadAndLines = async () => {
    if (!resolvedHeadId) return;

    showLoader();
    try {
      const [headRes, linesRes] = await Promise.all([
        wmsBaseService.getRow(resolvedHeadId, { wmsType }),
        wmsBaseService.getLinesByHeadId(resolvedHeadId, { wmsType }),
      ]);

      const nextHead = headRes?.row ?? headRes?.data ?? headRes ?? null;
      const nextLines = Array.isArray(linesRes?.rows) ? linesRes.rows : Array.isArray(linesRes) ? linesRes : [];

      setHead(nextHead);
      setLines(nextLines);
      await fetchBaseDocLines(nextHead?.baseId);
    } catch (e) {
      addNotification({
        content: 'A dokumentum adatai nem tölthetők be.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchHeadAndLines();
  }, [resolvedHeadId]);

  useEffect(() => {
    if (!Object.keys(config ?? {}).length) {
      getConfig();
    }
  }, [config, getConfig]);

  useEffect(() => {
    const loadMasterData = async () => {
      showLoader();
      try {
        const [warehouse, company] = await Promise.all([
          masterDataService.warehouseToForm(),
          masterDataService.companyToForm(),
        ]);
        const nextWarehouseData = Array.isArray(warehouse) ? warehouse : [];
        setWarehouseData(nextWarehouseData);
        setWarehouseOptions(generateAutocompleteItems(nextWarehouseData, 'name', 'code'));
        setCompanyData(Array.isArray(company) ? company : []);
      } finally {
        hideLoader();
      }
    };

    loadMasterData();
  }, []);

  useEffect(() => {
    setReference(head?.reference ?? '');
    setNote(head?.note ?? '');
  }, [head]);

  const sourceWarehouse = useMemo(
    () => warehouseData.find((warehouse) => warehouse.code === head?.whsCode) ?? null,
    [warehouseData, head?.whsCode]
  );
  const sourceCompany = useMemo(
    () => companyData.find((company) => company.id === head?.companyId) ?? null,
    [companyData, head?.companyId]
  );
  const sourceWarehouseLabel = useMemo(
    () => [head?.whsCode, sourceWarehouse?.name].filter(Boolean).join(' - '),
    [head?.whsCode, sourceWarehouse?.name]
  );
  const sourceCompanyLabel = useMemo(
    () =>
      [sourceCompany?.code ?? head?.companyCode, sourceCompany?.name ?? head?.companyName].filter(Boolean).join(' - '),
    [sourceCompany?.code, sourceCompany?.name, head?.companyCode, head?.companyName]
  );
  const isSourceBinManaged = Boolean(sourceWarehouse?.binManaged);
  const sourceBins = sourceWarehouse?.bin ?? [];
  const sourceBinOptions = useMemo(() => generateAutocompleteItems(sourceBins, 'binCode', 'binId'), [sourceBins]);
  const selectedSourceBin = sourceBins.find((bin) => bin.binId === sourceBinId) ?? null;

  useEffect(() => {
    if (!isSourceBinManaged) {
      setSourceBinId(null);
      return;
    }

    const hasSelectedBin = sourceBins.some((bin) => bin.binId === sourceBinId);
    if (!hasSelectedBin) {
      setSourceBinId(head?.binId ?? null);
    }
  }, [isSourceBinManaged, sourceBins, sourceBinId, head?.binId]);

  const handleSearchIncomingItem = async () => {
    const query = incomingFilter.trim();
    if (!query) return;

    const normalizeScanValue = (value) =>
      String(value ?? '')
        .replace(/\s+/g, '')
        .trim()
        .toLowerCase();
    const normalizedQuery = normalizeScanValue(query);
    const matchedBin = (sourceBins ?? []).find((bin) => normalizeScanValue(bin?.binCode) === normalizedQuery);
    if (matchedBin && matchedBin.binId != null) {
      setSourceBinId(matchedBin.binId);
      setIncomingFilter('');
      addNotification({
        content: `Rakhely kiválasztva: ${matchedBin.binCode}`,
        type: NotificationType.SUCCESS,
      });
      return;
    }

    showLoader();
    try {
      const data = await itemService.findItem({ query, fullSearch: false });
      if (!Array.isArray(data) || data.length === 0) {
        addNotification({
          content: `A cikkszám nem található a rendszerben. (${query})`,
          type: NotificationType.WARNING,
        });
        return;
      }

      const exactMatch = data.find((item) => String(item?.itemCode ?? '').toLowerCase() === query.toLowerCase());
      const selected = exactMatch ?? data[0];

      setIncomingItem(selected);
      setIncomingQty('');
      setIncomingBatchSn('');
      setIncomingLot('');
      setIncomingFilter('');
    } catch (e) {
      addNotification({
        content: 'A cikkszám ellenőrzése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const isIncomingBatchManaged = useMemo(() => {
    const invMethod = incomingItem?.item?.invManagementMethod ?? incomingItem?.invManagementMethod;
    return invMethod === InvManagementMethod.BATCH;
  }, [incomingItem]);

  const incomingQtyUnitCode =
    incomingItem?.quantityUnitCode ?? incomingItem?.item?.quantityUnit?.code ?? incomingItem?.quantityUnit?.code ?? '';

  const handleAddIncomingLine = async () => {
    if (!incomingItem?.itemCode) {
      addNotification({ content: 'Nincs kiválasztott bejövő cikk.', type: NotificationType.WARNING });
      return;
    }

    if (Number(incomingQty) <= 0) {
      addNotification({ content: 'A mennyiségnek 0-nál nagyobbnak kell lennie.', type: NotificationType.WARNING });
      return;
    }

    if (isIncomingBatchManaged && !String(incomingBatchSn || '').trim()) {
      addNotification({ content: 'Sarzsos cikkhez kötelező a sarzs megadása.', type: NotificationType.WARNING });
      return;
    }

    if (isIncomingBatchManaged && !String(incomingLot || '').trim()) {
      addNotification({ content: 'Sarzsos cikkhez kötelező a LOT szám megadása.', type: NotificationType.WARNING });
      return;
    }

    if (isSourceBinManaged && !sourceBinId) {
      addNotification({
        content: 'Tárhelyes raktárnál a tárhelykód megadása kötelező.',
        type: NotificationType.WARNING,
      });
      return;
    }

    showLoader();
    try {
      const normalizedItemCode = String(incomingItem.itemCode ?? '')
        .trim()
        .toLowerCase();
      const matchedBaseLine = (baseDocLines ?? []).find((line) => {
        const lineItemCode = String(line?.itemCode ?? '')
          .trim()
          .toLowerCase();
        if (!lineItemCode || lineItemCode !== normalizedItemCode) return false;

        const openQty = Number(line?.qtyDiff ?? line?.qntQty ?? 0) || 0;
        return openQty > 0;
      });

      await wmsBaseService.createLine({
        wmsType,
        wmsHeadId: resolvedHeadId,
        itemCode: incomingItem.itemCode,
        quantity: roundToDecimalPlaces(Number(incomingQty), quantityDecimalPlaces),
        baseLine: matchedBaseLine?.lineNum ?? null,
        baseId: head?.baseId ?? null,
        baseDocNum: head?.baseDocNum ?? null,
        baseDocType: head?.baseDocType ?? null,
        whsCode: head?.whsCode ?? incomingItem?.whsCode ?? null,
        binId: sourceBinId ?? null,
        binCode: selectedSourceBin?.binCode ?? null,
        batchSn: isIncomingBatchManaged ? String(incomingBatchSn).trim() : null,
        b_lot: isIncomingBatchManaged ? String(incomingLot).trim() : null,
        quantityUnitId:
          incomingItem?.quantityUnitId ??
          incomingItem?.item?.quantityUnit?.id ??
          incomingItem?.quantityUnit?.id ??
          null,
        quantityUnitCode:
          incomingItem?.quantityUnitCode ??
          incomingItem?.item?.quantityUnit?.code ??
          incomingItem?.quantityUnit?.code ??
          null,
      });

      setIncomingItem(null);
      setIncomingQty('');
      setIncomingBatchSn('');
      setIncomingLot('');
      await fetchHeadAndLines();
    } catch (e) {
      addNotification({ content: 'A bejövő tétel rögzítése sikertelen.', type: NotificationType.ERROR });
    } finally {
      hideLoader();
    }
  };

  const handleRecognizeBaseDocItem = async (line) => {
    const itemCode = String(line?.itemCode ?? '').trim();
    if (!itemCode) {
      addNotification({ content: 'A sor nem tartalmaz cikkszámot.', type: NotificationType.WARNING });
      return;
    }

    showLoader();
    try {
      const data = await itemService.findItem({ query: itemCode, fullSearch: false });
      if (!Array.isArray(data) || data.length === 0) {
        addNotification({ content: `A cikk nem található: ${itemCode}`, type: NotificationType.WARNING });
        return;
      }

      const exactMatch = data.find((item) => String(item?.itemCode ?? '').toLowerCase() === itemCode.toLowerCase());
      const selected = exactMatch ?? data[0];

      setIncomingItem(selected);
      setIncomingQty('');
      setIncomingBatchSn('');
      setIncomingLot('');
      setIncomingFilter('');

      addNotification({ content: `Cikk felismerve: ${selected?.itemCode}`, type: NotificationType.SUCCESS });
    } catch (e) {
      addNotification({ content: 'A cikk felismerése sikertelen.', type: NotificationType.ERROR });
    } finally {
      hideLoader();
    }
  };

  const handleClearIncomingItem = () => {
    setIncomingItem(null);
    setIncomingQty('');
    setIncomingBatchSn('');
    setIncomingLot('');
  };

  const handleDeleteLine = async (line) => {
    const lineId = line?.id;
    if (!lineId) return;

    showLoader();
    try {
      await wmsBaseService.deleteLine(lineId, wmsType);
      await fetchHeadAndLines();
    } catch (e) {
      addNotification({
        content: 'A sor törlése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const handleSaveDoc = async () => {
    if (!head || !resolvedHeadId) return;

    showLoader();
    try {
      const newDocNum = await wmsBaseService.generateTargetDoc(resolvedHeadId, {
        wmsType,
        reference,
        note,
      });

      navigate('/app/wms/purchaseDelivery');

      addNotification({
        content: `Sikeres mentés!`,
        type: NotificationType.SUCCESS,
      });
    } catch (e) {
      addNotification({
        content: 'A bizonylat mentése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const tabs = [
    ...(head?.baseId
      ? [
          {
            id: 0,
            name: 'QUANT',
            content: (
              <PurchaseDeliveryBaseDocTab baseDocLines={baseDocLines} onRecognizeItem={handleRecognizeBaseDocItem} />
            ),
          },
        ]
      : []),
    {
      id: 1,
      name: 'WMS',
      content: <WmsTabContent lines={lines} onDeleteLine={handleDeleteLine} />,
    },
    {
      id: 2,
      name: 'Lezárás',
      content: (
        <TargetDocTab
          reference={reference}
          onReferenceChange={setReference}
          note={note}
          onNoteChange={setNote}
          onSave={handleSaveDoc}
        />
      ),
    },
  ];

  return (
    <PhoneBase name="PurchaseDelivery">
      <Typography variant="h5" sx={{ fontWeight: 400, py: 1, px: 2 }}>
        Árubeérkezés
      </Typography>
      <Box sx={{ px: 2, pb: 1 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Raktár: {sourceWarehouseLabel || '-'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                ÜP: {sourceCompanyLabel || head?.companyId || '-'}
              </Typography>
              {head?.baseDocNum ? (
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Bizonylat: {head.baseDocNum}
                </Typography>
              ) : null}
            </Grid>

            {isSourceBinManaged ? (
              <Grid item xs={9} md="auto" sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Box sx={{ minWidth: 200, width: '100%', maxWidth: 360, bgcolor: 'white' }}>
                  <AutoCompleteSelectUI
                    selectedValue={sourceBinId}
                    selectedLabelValue={selectedSourceBin?.binCode ?? ''}
                    onChange={(_e, newVal) => {
                      setSourceBinId(newVal?.value ?? null);
                    }}
                    dataset={sourceBinOptions}
                    placeholder="Rakhely"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Box>

        <IncomingItem
          incomingFilter={incomingFilter}
          onIncomingFilterChange={setIncomingFilter}
          onSearchIncomingItem={handleSearchIncomingItem}
          incomingItem={incomingItem}
          onClearIncomingItem={handleClearIncomingItem}
          isIncomingBatchManaged={isIncomingBatchManaged}
          incomingBatchSn={incomingBatchSn}
          onIncomingBatchSnChange={setIncomingBatchSn}
          incomingLot={incomingLot}
          onIncomingLotChange={setIncomingLot}
          incomingQty={incomingQty}
          onIncomingQtyChange={(rawValue) => {
            if (rawValue === '') {
              setIncomingQty('');
              return;
            }
            const parsedQty = Number(rawValue);
            if (Number.isNaN(parsedQty)) return;
            setIncomingQty(roundToDecimalPlaces(Math.max(0, parsedQty), quantityDecimalPlaces));
          }}
          quantityInputStep={quantityInputStep}
          incomingQtyUnitCode={incomingQtyUnitCode}
          onAddIncomingLine={handleAddIncomingLine}
        />
      </Box>

      <TabsUI activeTab={activeTab} handleTabClick={setActiveTab} disablePadding>
        {tabs.map((tab) => (
          <div key={tab.id} name={tab.name}>
            {tab.content}
          </div>
        ))}
      </TabsUI>
    </PhoneBase>
  );
};

export default PurchaseDeliveryDoc;
